import React from "react";
import { KgArtifactNodeCard } from "../KgArtifactNodeCard";
import { KgDataModelNodeCard } from "../KgDataModelNodeCard";
import { KgDataSourceNodeCard } from "../KgDataSourceNodeCard";
import { KgDataStoryNodeCard } from "../KgDataStoryNodeCard";
import { KgMetricNodeCard } from "../KgMetricNodeCard";
import { KgOntologyNodeCard } from "../KgOntologyNodeCard";
import { KgReportNodeCard } from "../KgReportNodeCard";
import { KgTableNodeCard } from "../KgTableNodeCard";
import type { KgFlowNode } from "../../kg/canvas/types";
import { TileWrap } from "./KgCatalogEntityTile.styles";
import type { KgCatalogEntityTileProps } from "./KgCatalogEntityTile.types";

const renderNodeCard = (
  node: KgFlowNode,
  expandedTableIds?: Set<string>,
  onTableTileToggle?: (nodeId: string) => void,
  onNodeClick?: (node: KgFlowNode) => void,
  isSelected?: boolean,
): React.ReactNode => {
  const { id, type, data } = node;
  const title = data.title ?? id;

  switch (type) {
    case "dataSource":
      return (
        <KgDataSourceNodeCard
          title={title}
          subtitle={data.subtitle}
          logoUrl={data.logoUrl}
          iconColor={data.iconColor}
          visualState={data.visualState}
          isSelected={isSelected}
        />
      );
    case "table": {
      const isExpanded = expandedTableIds?.has(id) ?? false;
      return (
        <KgTableNodeCard
          tableName={title}
          fields={data.fields ?? []}
          expanded={isExpanded}
          visualState={data.visualState}
          isSelected={isSelected ?? isExpanded}
          onClick={
            onTableTileToggle
              ? (event) => {
                  event.stopPropagation();
                  onTableTileToggle(id);
                  onNodeClick?.(node);
                }
              : undefined
          }
        />
      );
    }
    case "dataModel":
      return (
        <KgDataModelNodeCard
          name={title}
          visualState={data.visualState}
          isSelected={isSelected}
        />
      );
    case "ontology":
      return (
        <KgOntologyNodeCard
          name={title}
          hasWarning={data.hasWarning}
          visualState={data.visualState}
          isSelected={isSelected}
        />
      );
    case "metric":
      return (
        <KgMetricNodeCard
          name={title}
          hasWarning={data.hasWarning}
          visualState={data.visualState}
          isSelected={isSelected}
        />
      );
    case "report":
      return (
        <KgReportNodeCard
          name={title}
          biName={data.biName}
          biLogoUrl={data.biLogoUrl}
          visualState={data.visualState}
          isSelected={isSelected}
        />
      );
    case "dataStory":
      return (
        <KgDataStoryNodeCard
          name={title}
          status={data.status}
          visualState={data.visualState}
          isSelected={isSelected}
        />
      );
    case "artifact":
      return (
        <KgArtifactNodeCard
          name={title}
          kind={data.kind}
          visualState={data.visualState}
          isSelected={isSelected}
        />
      );
    default:
      return null;
  }
};

export const KgCatalogEntityTile: React.FC<KgCatalogEntityTileProps> = ({
  node,
  expandedTableIds,
  onTableTileToggle,
  onNodeClick,
  isSelected,
  className,
}) => (
  <TileWrap
    className={className}
    onClick={() => onNodeClick?.(node)}
    data-testid={`kg-catalog-tile-${node.id}`}
  >
    {renderNodeCard(node, expandedTableIds, onTableTileToggle, onNodeClick, isSelected)}
  </TileWrap>
);

export default KgCatalogEntityTile;
